import { useCallback, useState } from 'react';
import { processDocument } from '../utils/documentUtils';

const createDocumentId = () => Date.now() + Math.random().toString(36).substr(2, 9);

const useDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    const validFileTypes = [
      'application/pdf', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    
    const validFiles = fileArray.filter(file => 
      validFileTypes.includes(file.type)
    );
    
    if (validFiles.length !== fileArray.length) {
      alert('Only PDF, DOCX, and DOC files are allowed.');
    }
    
    if (validFiles.length > 0) {
      setIsUploading(true);
      
      try {
        // Process each file
        const processedDocs = await Promise.all(
          validFiles.map(async (file) => {
            try {
              // Process the file to extract text
              const { content, pageCount } = await processDocument(file);
              
              return {
                id: createDocumentId(),
                name: file.name,
                size: file.size,
                type: file.type,
                tokenized: true,
                content: content,
                pageCount: pageCount
              };
            } catch (error) {
              console.error(`Error processing ${file.name}:`, error);
              
              // Return a document with error information
              return {
                id: createDocumentId(),
                name: file.name,
                size: file.size,
                type: file.type,
                tokenized: false,
                content: `[Error processing ${file.name}: ${error.message}]`,
                error: true
              };
            }
          })
        );
        
        // Add processed documents to state
        setDocuments(prevDocs => [...prevDocs, ...processedDocs]);
      } catch (error) {
        console.error('Error processing documents:', error);
        alert('Error processing documents. Please try again.');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const addProtocolDocument = useCallback((protocolContent, sourceGuid = '') => {
    if (typeof protocolContent !== 'string' || protocolContent.trim() === '') {
      throw new Error('Protocol content is required to preload a document.');
    }

    const content = protocolContent.trim();
    const protocolName = sourceGuid
      ? `IRB Protocol (${sourceGuid})`
      : 'IRB Protocol';

    const protocolDocument = {
      id: createDocumentId(),
      name: protocolName,
      size: content.length,
      type: 'text/plain',
      tokenized: true,
      content,
      pageCount: null
    };

    setDocuments(prevDocs => [...prevDocs, protocolDocument]);
    return protocolDocument;
  }, []);
  
  const removeDocument = (docId) => {
    setDocuments(prevDocs => prevDocs.filter(doc => doc.id !== docId));
  };
  
  return {
    documents,
    isUploading,
    handleFiles,
    removeDocument,
    addProtocolDocument
  };
};

export default useDocuments;
